import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';
import { v4 } from 'uuid';

import type { TDictObject } from 'src/api/directories/types';
import { hasValue } from 'src/shared/utils/common';
import { debounce } from 'src/shared/utils/debounce';
import type { Directories } from 'src/store/directories/directories.store';
import type { SystemSettingsService } from 'src/store/system-settings/system-settings.store';

import type { ISystemSettingsStore } from '../../types';

export const DIRECTORY_TYPES = [
  'Common_Cluster',
  'Common_Block',
  'Common_Field',
  'Common_Organization',
  'Common_WellPurpose',
  'Common_FluidType',
];
export type TPaletteSettingsData = TDictObject<Record<string, Record<number, string>>>;

export class PaletteSettingItem {
  readonly entityId = v4();

  readonly id: number;
  readonly name: string;

  @observable color: string | null = null;

  constructor(id: number, name: string, color: string | null) {
    this.id = id;
    this.name = name;
    this.color = color;

    makeObservable(this);
  }

  setColor(color: string | null): void {
    this.color = color;
  }
}

export class PaletteSettingsStore implements ISystemSettingsStore {
  private readonly directories: Directories;

  private readonly settingsStore: SystemSettingsService;
  readonly section = 'palette' as const;
  private settings: TPaletteSettingsData | null = null;

  @observable.ref settingsItems: Record<
    string,
    {
      active: PaletteSettingItem[];
      archived: PaletteSettingItem[];
    }
  > = {};
  @observable currentObject: PaletteSettingItem | null = null;

  @observable searchValue: string = '';
  @observable isLoading = false;
  @observable isInitiated = false;
  @observable dictsStatus: 'active' | 'archived' = 'active';

  constructor(directories: Directories, settingsStore: SystemSettingsService) {
    this.directories = directories;
    this.settingsStore = settingsStore;

    makeObservable(this);
  }

  @computed
  get subSection(): string | null {
    return this.settingsStore.subSection;
  }

  @computed
  get subSections(): { id: string; name: string }[] {
    return DIRECTORY_TYPES.map((objType) => {
      return {
        id: objType,
        name: `directories:DirectoryNames.${objType}`,
      };
    });
  }

  private async loadSettings() {
    return await this.settingsStore.loadSettings<TPaletteSettingsData>(this.section);
  }

  private async loadDirectories() {
    try {
      const [objects] = await Promise.all([
        this.directories.loadObjects(DIRECTORY_TYPES),
        this.directories.loadObjects(['Common_OrganizationActivity']),
      ]);

      return objects;
    } catch (e) {
      console.error(e);
    }
  }

  @action.bound
  setSearchValue(searchValue: string): void {
    this.searchValue = searchValue;
  }

  @action.bound
  setCurrentObject(item: PaletteSettingItem | null): void {
    this.currentObject = item;
  }

  @action.bound
  setDictsStatus(status: 'active' | 'archived'): void {
    this.dictsStatus = status;
  }

  @action.bound
  private _updateData(): void {
    if (!this.settings) {
      console.error('settings is not set');
      return;
    }

    const serializedData = Object.entries(this.settingsItems).reduce((acc, [objectType, { active, archived }]) => {
      const objectTypeData: Record<number, string> = {};

      active.forEach((item) => {
        if (!item.color) {
          return;
        }

        objectTypeData[item.id] = item.color;
      });

      archived.forEach((item) => {
        if (!item.color) {
          return;
        }

        objectTypeData[item.id] = item.color;
      });

      return {
        ...acc,
        [objectType]: objectTypeData,
      };
    }, {});

    this.settingsStore.updateSettings(this.section, this.settings.id as number, {
      ...this.settings.data,
      ...serializedData,
    });
  }

  private processDictObjects(objectType: string, objects: TDictObject[]): TDictObject[] {
    switch (objectType) {
      case 'Common_Organization': {
        const organizationActivities = this.directories.getObject('Common_OrganizationActivity');

        if (!organizationActivities || !organizationActivities.length) {
          return objects;
        }

        const subsoilUserActivityId = organizationActivities.find((oa) => oa.data['slug'] === 'suboil-user')?.id;

        if (!hasValue(subsoilUserActivityId)) {
          return objects;
        }

        return objects.filter((obj) => obj.data['organizationActivityId'] === subsoilUserActivityId);
      }

      default: {
        return objects;
      }
    }
  }

  @flow.bound
  private async *initData() {
    const [objects, settings] = await Promise.all([this.loadDirectories(), this.loadSettings()]);
    yield;

    if (!objects || !settings) {
      console.error('failed to load directories or settings', objects, settings);
      return;
    }

    this.settings = settings;

    const settingsItems = objects.reduce(
      (
        acc: Record<
          string,
          {
            active: PaletteSettingItem[];
            archived: PaletteSettingItem[];
          }
        >,
        obj,
        index,
      ) => {
        const objectType = DIRECTORY_TYPES[index];

        if (!objectType) {
          console.error('cannot find objectType');
          return acc;
        }

        const processedObjects = this.processDictObjects(objectType, obj);

        const { archived, active } = processedObjects.reduce(
          (
            acc: {
              active: PaletteSettingItem[];
              archived: PaletteSettingItem[];
            },
            dictValue,
          ) => {
            const color = settings.data[objectType]?.[dictValue.id as number] ?? null;

            const item = new PaletteSettingItem(
              dictValue.id as number,
              dictValue.data['name']?.toString() ?? '',
              color,
            );

            if (dictValue.status === 'ACTIVE') {
              acc.active.push(item);
            } else {
              acc.archived.push(item);
            }

            return acc;
          },
          { active: [], archived: [] },
        );

        if (!acc[objectType]) {
          acc[objectType] = {
            active: [],
            archived: [],
          };
        }

        acc[objectType].active = [...(acc[objectType]?.active ?? []), ...active].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        acc[objectType].archived = [...(acc[objectType]?.archived ?? []), ...archived].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        return acc;
      },
      {},
    );

    this.settingsItems = settingsItems;
  }

  @action.bound
  setColor(objectType: string, _item: PaletteSettingItem | string, color: string | null): void {
    const settingsItem = (() => {
      if (typeof _item === 'string') {
        return (
          this.settingsItems[objectType]?.active.find((i) => i.entityId === _item) ??
          this.settingsItems[objectType]?.archived.find((i) => i.entityId === _item)
        );
      }

      return _item;
    })();

    if (!settingsItem) {
      console.error('settingsItem not found');
      return;
    }

    if (settingsItem.color === color) {
      return;
    }

    settingsItem.setColor(color);

    this.updateData();
  }

  updateData = debounce(() => this._updateData(), 500);

  init = () => {
    this.initData();

    const changeSubSectionDisposer = reaction(
      () => this.subSection,
      () => {
        this.setCurrentObject(null);
      },
    );

    return () => {
      changeSubSectionDisposer();
    };
  };
}
